/**
 * Unlock Content UseCase
 *
 * Executes the unlock content flow for a given media item and current user.
 * Validates preconditions via canUnlockContent before delegating to the service.
 *
 * Flutter: UnlockContentBottomSheet._unlockVideo()
 */
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { UnlockContentService } from '@/domain/services/content/unlockContentService';
import { RefreshWalletUseCase } from '@/domain/usecases/wallet/refreshWalletUseCase';
import { SaveUnlockContentToSecureStorageUseCase } from '@/domain/usecases/content/saveUnlockContentToSecureStorageUseCase';

export class UnlockContentUseCase {
  constructor(private readonly unlockContentService: UnlockContentService) {}

  async execute(
    media: UserMediaEntity,
    currentUser: UserEntity,
    hasUnlockedContent: boolean,
    hasActiveSubscription: boolean,
  ): Promise<boolean> {
    const isOwner = currentUser.firebaseId === media.firebaseId;

    const canUnlock = this.unlockContentService.canUnlockContent({
      allowUnlock: media.allowUnlock,
      unlockCharge: media.unlockCharge,
      isOwner,
      hasUnlockedContent,
      hasActiveSubscription,
      typeLimit: media.typeLimit,
      duration: media.duration,
    });

    if (!canUnlock) return false;

    const success = await this.unlockContentService.executeUnlock(media, currentUser);

    if (success) {
      await new SaveUnlockContentToSecureStorageUseCase().execute(media.relativePath!);
      // Coins were deducted server-side — refresh local wallet to sync Redux state
      await new RefreshWalletUseCase().execute();
    }

    return success;
  }
}
